import React, { FunctionComponent } from 'react'
import { Platform, useWindowDimensions } from 'react-native'
import Svg, { Path } from 'react-native-svg'

import styled, { useTheme } from 'styled-components/native'

import { breakpoints } from '../../constants'

const HalfMoonBackground = styled.View<{ marginBottom: string; marginTop: string }>`
  width: 100%;
  position: relative;
  z-index: 10;
  margin-bottom: ${({ marginBottom }) => marginBottom};
  margin-top: ${({ marginTop }) => marginTop};
`

const StyledSVG = styled(Svg)`
  position: absolute;
  top: -76px;
  width: 100%;
`

const StyledSVGSimplified = styled(Svg)({
  position: 'absolute',
  bottom: '-1px',
})

export type HalfMoonDividerProps = {
  color?: string
  getSpecificDimensions?: () => {
    height: number | string
    marginBottom: string
    marginTop: string
    width?: number | string
  }
  customCurvePath?: string
  /** Curve is aligned to the bottom of the viewbox so less custom positioning is needed */
  useSimplifiedCurve?: boolean
}

const getPlatformSpecificPath = () => {
  switch (Platform.OS) {
    case 'ios':
      return 'M 0 0 L 0 0 Q 250 60 500 -38 L 500 30 H 0 Z'
    case 'android':
      return 'M 0 0 L 0 0 Q 250 50 500 -9 L 500 30 H 0 Z'
    default:
      return 'M 0 0 L 0 0 Q 250 200 500 0 L 500 100 H 0 Z'
  }
}

const getPlatformSpecificDimensions = (width: number) => {
  switch (Platform.OS) {
    case 'ios':
    case 'android':
      return {
        height: 80,
        marginBottom: '-52px',
        marginTop: '90px',
        width: 500,
      }
    default:
      if (width < breakpoints.laptop) {
        return {
          height: 40,
          marginBottom: '-37px',
          marginTop: '80px',
          width: 500,
        }
      }

      if (width < breakpoints.desktop) {
        return {
          height: 80,
          marginBottom: '3px',
          marginTop: '40px',
          width: 500,
        }
      }

      return {
        height: 100,
        marginBottom: '23px',
        marginTop: '20px',
        width: 500,
      }
  }
}

export const HalfMoonDivider: FunctionComponent<HalfMoonDividerProps> = ({
  getSpecificDimensions,
  customCurvePath,
  color,
  useSimplifiedCurve = false,
}) => {
  const { width: windowWidth } = useWindowDimensions()
  const platformSpecificPath = customCurvePath ?? getPlatformSpecificPath()
  const { height, marginBottom, marginTop, width } = getSpecificDimensions
    ? getSpecificDimensions()
    : getPlatformSpecificDimensions(windowWidth)
  const { colors } = useTheme()

  return useSimplifiedCurve ? (
    <StyledSVGSimplified width={'100%'} height={'15%'} viewBox={`0 0 500 47`} fill='none' preserveAspectRatio='none'>
      <Path d='M0 0 C125 60 375 60 500 0 L500 100 L0 100 Z' fill={color || colors.backgroundSecondary} />
    </StyledSVGSimplified>
  ) : (
    <HalfMoonBackground marginTop={marginTop} marginBottom={marginBottom}>
      <StyledSVG width={width} height={height} viewBox='0 0 500 100' preserveAspectRatio='none' pointerEvents='none'>
        <Path d={platformSpecificPath} fill={color || colors.backgroundSecondary} />
      </StyledSVG>
    </HalfMoonBackground>
  )
}
